import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore, useAgentStore, useToastStore } from '@/store';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import useStartAgentWorkChat from '@/hooks/useStartAgentWorkChat';
import useStartAgentChat from '@/hooks/useStartAgentChat';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuItemDivider from '@/components/DropdownMenu/DropdownMenuItemDivider';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import useCopyAgentLink from '@/hooks/useCopyAgentLink';
import { useModalHelper } from '@/hooks/useModalHelper';

function AgentDropdown({
  agentId,
  isSaved,
  onToggleSave,
  TriggerComponent,
  trigger,
  open,
  onOpenChange,
  noRoot,
  isLongPress,
}) {
  const { openModal } = useModalStore(state => state.actions);
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();
  const { copyAgentLink } = useCopyAgentLink();
  const requireAuth = useRequireAuth();
  const startAgentWorkChat = useStartAgentWorkChat();
  const startAgentChat = useStartAgentChat();
  const agent = useAgentStore(React.useCallback(state => state.getAgentById(agentId), [agentId]));
  const deleteAgent = useAgentStore(state => state.deleteAgent);
  const addToast = useToastStore(state => state.actions.addToast);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  const handleNewChat = useCallback((event) => {
    event?.stopPropagation?.();
    return startAgentChat(agent, { source: 'bot_menu_new_chat' });
  }, [startAgentChat, agent]);

  const handleNewWorkChat = useCallback((event) => {
    event?.stopPropagation?.();
    return startAgentWorkChat(agent, { source: 'bot_menu_new_work_chat' });
  }, [startAgentWorkChat, agent]);

  const handleCreateTask = useCallback((event) => {
    event?.stopPropagation?.();
    if (!agent?.id) return;
    return requireAuth(() => openModal('CreateTaskModal', { agentId: agent.id, agentName: agent.name }), { source: 'bot_menu_create_task' });
  }, [agent, requireAuth, openModal]);

  const handleEdit = useCallback((event) => {
    event?.stopPropagation?.();
    if (agent?.isOwner) openModal('CustomAgentModal', { agentId });
  }, [openModal, agentId, agent?.isOwner]);

  const handleDelete = useCallback((event) => {
    event?.stopPropagation?.();
    if (!agent?.isOwner || !agent?.id) return;
    showConfirmationModal({
      title: t('pages.agents.deleteAgentTitle').replace('%NAME%', agent.name),
      message: t('pages.agents.deleteAgentMessage'),
      confirmText: t('pages.agents.deleteAgent'),
      cancelText: t('common.cancel'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteAgent(agent.id);
          addToast(t('pages.agents.agentDeleted'), 'success');
        } catch (error) {
          console.error('Failed to delete agent:', error);
          addToast(t('pages.agents.deleteAgentFailed'), 'error');
        }
      },
    });
  }, [agent, showConfirmationModal, deleteAgent, addToast, t]);

  const handleViewDetails = useCallback((event) => {
    event?.stopPropagation?.();
    openModal('AgentDetailsModal', { agentId });
  }, [openModal, agentId]);

  const handleCopyLink = useCallback((event) => {
    event?.stopPropagation?.();
    copyAgentLink(agent || agentId);
  }, [copyAgentLink, agent, agentId]);

  const handleToggleSave = useCallback((event) => {
    event?.stopPropagation?.();
    return onToggleSave?.();
  }, [onToggleSave]);

  const content = (
    <>
      {(trigger || TriggerComponent) && (
        <DropdownMenu.Trigger asChild={!!trigger} action={isLongPress ? 'longPress' : undefined}>
          {trigger || <TriggerComponent />}
        </DropdownMenu.Trigger>
      )}

      <DropdownMenu.Content 
        className={Platform.OS === 'web' ? "DropdownMenuContent" : undefined}
        align={isXS ? 'center' : 'end'} 
        sideOffset={isXS ? 4 : 5}
        onClick={event => event.stopPropagation()}
        onPointerDown={event => event.stopPropagation()}
        onCloseAutoFocus={event => event.preventDefault()}
      >
        {Platform.OS === 'web' ? (
          // Web implementation using DropdownMenuItem
          <>
            {agent?.name && (
              <>
                <DropdownMenuLabel key="agent-title" text={agent.name} />
                <DropdownMenuItemDivider key="agent-title-divider" />
              </>
            )}
            {agent?.id && (
              <DropdownMenuItem
                key="new-chat"
                onSelect={handleNewChat}
                icon="comment-dots"
                title={t('nav.routes.agentChat.newChat')}
              />
            )}
            {agent?.id && (
              <DropdownMenuItem
                key="new-work-chat"
                onSelect={handleNewWorkChat}
                icon="pen-to-square"
                title={t('nav.routes.agentChat.newWorkChat')}
              />
            )}
            {agent?.id && (
              <DropdownMenuItem
                key="create-task"
                onSelect={handleCreateTask}
                icon="calendar"
                title={t('tasks.createScheduledTask')}
              />
            )}
            {agent?.isOwner && (
              <DropdownMenuItem
                key="edit"
                onSelect={handleEdit}
                icon="pencil"
                title={t('pages.agents.editAgent')}
              />
            )}
            {!agent?.isOwner && (
            <DropdownMenuItem
              key="save"
              onSelect={handleToggleSave}
              icon="star"
              title={isSaved ? t('dropdowns.unsave') : t('common.save')}
            />
            )}
            <DropdownMenuItem
              key="copy-link"
              onSelect={handleCopyLink}
              icon="link"
              title={t('dropdowns.copyLink')}
            />
            <DropdownMenuItem
              key="details"
              onSelect={handleViewDetails}
              icon="circle-info"
              title={t('dropdowns.seeAgentDetails')}
            />
            {agent?.isOwner && (
              <>
                <DropdownMenuItemDivider />
                <DropdownMenuItem
                  key="delete"
                  onSelect={handleDelete}
                  icon="trash"
                  title={t('pages.agents.deleteAgent')}
                  isDanger
                />
              </>
            )}
          </>
        ) : (
          // Native implementation using native components
          <>
            {agent?.name && (
              <>
                <DropdownMenu.Item key="agent-title" disabled>
                  <DropdownMenu.ItemTitle numberOfLines={1}>{agent.name}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
                <DropdownMenu.Separator />
              </>
            )}
            {agent?.id && (
              <DropdownMenu.Item key="new-chat" onSelect={handleNewChat}>
                <DropdownMenu.ItemIcon ios={{ name: 'bubble.left', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{t('nav.routes.agentChat.newChat')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {agent?.id && (
              <DropdownMenu.Item key="new-work-chat" onSelect={handleNewWorkChat}>
                <DropdownMenu.ItemIcon ios={{ name: 'square.and.pencil', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{t('nav.routes.agentChat.newWorkChat')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {agent?.id && (
              <DropdownMenu.Item key="create-task" onSelect={handleCreateTask}>
                <DropdownMenu.ItemIcon ios={{ name: 'calendar.badge.plus', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{t('tasks.createScheduledTask')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {agent?.isOwner && (
              <DropdownMenu.Item key="edit" onSelect={handleEdit}>
                <DropdownMenu.ItemIcon ios={{ name: 'pencil', color: colors.text_color_primary }} />
                <DropdownMenu.ItemTitle>{t('pages.agents.editAgent')}</DropdownMenu.ItemTitle>
              </DropdownMenu.Item>
            )}
            {!agent?.isOwner && (
            <DropdownMenu.Item key="save" onSelect={handleToggleSave}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "star",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {isSaved ? t('dropdowns.unsave') : t('common.save')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            )}
            <DropdownMenu.Item key="copy-link" onSelect={handleCopyLink}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "link",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.copyLink')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            <DropdownMenu.Item key="details" onSelect={handleViewDetails}>
              <DropdownMenu.ItemIcon ios={{ 
                name: "info.circle",
                color: colors.text_color_primary 
              }} />
              <DropdownMenu.ItemTitle>
                {t('dropdowns.seeAgentDetails')}
              </DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
            {agent?.isOwner && (
              <>
                <DropdownMenu.Separator />
                <DropdownMenu.Item key="delete" destructive onSelect={handleDelete}>
                  <DropdownMenu.ItemIcon ios={{ name: 'trash', color: colors.color_destructive }} />
                  <DropdownMenu.ItemTitle>{t('pages.agents.deleteAgent')}</DropdownMenu.ItemTitle>
                </DropdownMenu.Item>
              </>
            )}
          </>
        )}
      </DropdownMenu.Content>
    </>
  );

  if (noRoot) {
    return content;
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={onOpenChange}>
      {content}
    </DropdownMenu.Root>
  );
}

export default React.memo(AgentDropdown);
